import { defineStore } from 'pinia';
import { ref } from 'vue';
import { RuntimeConfig } from '@vp/common/api';

type WidgetKind = 'invisible' | 'managed';

declare global {
  interface Window {
    turnstile?: any;
  }
}

/** Thrown when the customer dismisses the Fan Verification modal. Not a failure. */
export class TurnstileCancelledError extends Error {}

export const useTurnstileStore = defineStore('turnstile', () => {
  const ready = ref(false);
  const warmflight = ref<Promise<void> | null>(null);

  const open = ref(false);
  const inflight = ref<Promise<void> | null>(null);

  const humanToken = ref<string | null>(null);

  const resolveCb = ref<(() => void) | null>(null);
  const rejectCb = ref<((reason?: unknown) => void) | null>(null);

  const ensureScript = () => {
    if (typeof window === 'undefined') {
      return Promise.resolve();
    }

    if (window.turnstile) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        resolve();
      };
      script.onerror = () => {
        reject(new Error('Failed to load Turnstile script'));
      };
      document.head.appendChild(script);
    });
  };

  const runInvisibleOnce = async () => {
    await ensureScript();
    const { turnstile } = await RuntimeConfig.getRuntimeConfig();

    const container = document.createElement('div');
    container.className = 'cf-turnstile';
    container.setAttribute('data-sitekey', turnstile.invisibleSiteKey ?? '');
    container.style.cssText = 'position: absolute; left: -9999px; top: -9999px; width: 1px; height: 1px; overflow: hidden;';
    document.body.appendChild(container);

    try {
      const token = await new Promise<string>((resolve, reject) => {
        const ts = window.turnstile;
        const id = ts.render(container, {
          callback: (token: string) => resolve(token),
          'error-callback': () => reject(new Error('Turnstile error')),
          retry: 'auto',
        });
        ts.execute(id);
      });

      await verifyToken(token, 'invisible');
      ready.value = true;
    } finally {
      container.remove();
    }
  };

  const verifyToken = async (token: string, widget: WidgetKind) => {
    const r = await fetch('/turnstile/verify', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        token,
        widget,
      }),
    });

    const body = await r.json().catch(() => ({}));

    if (!r.ok || !body?.success) {
      throw new Error(body?.message || 'Token verification failed');
    }

    if (body.token) {
      humanToken.value = body.token;
    }
  };

  const warmup = async () => {
    if (ready.value) {
      return Promise.resolve();
    }

    if (warmflight.value) {
      return warmflight.value;
    }

    warmflight.value = runInvisibleOnce()
      .catch(() => { /* Ignore. If it fails, user can still solve manually */ })
      .finally(() => warmflight.value = null);

    return warmflight.value;
  };

  const solve = (): Promise<void> => {
    if (inflight.value) {
      return inflight.value;
    }

    open.value = true;
    inflight.value = new Promise<void>((resolve, reject) => {
      resolveCb.value = resolve;
      rejectCb.value = reject;
    }).finally(() => {
      inflight.value = null;
    });

    return inflight.value;
  };

  const resolved = () => {
    open.value = false;
    ready.value = true;
    resolveCb.value?.();
    resolveCb.value = null;
    rejectCb.value = null;
  };

  const failed = (err?: unknown) => {
    open.value = false;
    rejectCb.value?.(err ?? new Error('Turnstile challenge failed'));
    resolveCb.value = null;
    rejectCb.value = null;
  };

  return {
    ready,
    warmflight,

    open,
    inflight,

    humanToken,

    ensureScript,
    verifyToken,
    warmup,
    solve,
    resolved,
    failed,
  };
});
